import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { IGetCompaniesRes } from '../services/company/model';

type CompanyContextType = {
  selectedCompany: IGetCompaniesRes | null;
  setSelectedCompany: (company: IGetCompaniesRes) => void;
};

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const SELECTED_COMPANY_KEY = 'selectedCompany';

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const [selectedCompany, setSelectedCompanyState] = useState<IGetCompaniesRes | null>(() => {
    try {
      const savedCompany = localStorage.getItem(SELECTED_COMPANY_KEY);
      return savedCompany ? JSON.parse(savedCompany) : null;
    } catch (error) {
      console.warn('Error reading selected company from localStorage:', error);
      return null;
    }
  });

  const setSelectedCompany = (company: IGetCompaniesRes) => {
    try {
      setSelectedCompanyState(company);
      localStorage.setItem(SELECTED_COMPANY_KEY, JSON.stringify(company));
    } catch (error) {
      console.warn('Error saving selected company to localStorage:', error);
      setSelectedCompanyState(company);
    }
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SELECTED_COMPANY_KEY && event.newValue) {
        try {
          const company = JSON.parse(event.newValue);
          setSelectedCompanyState(company);
        } catch (error) {
          console.warn('Error parsing company from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <CompanyContext.Provider value={{ selectedCompany, setSelectedCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) throw new Error('useCompany must be used within a CompanyProvider');
  return context;
};